import { useId, type InputHTMLAttributes } from 'react';

/**
 * Controlled-text input для форм онбординга и других. Block 0 Phase 2 / Step 3.
 *
 * P-022 a11y baseline:
 * - `<label htmlFor={id}>` связан с `<input id={id}>`.
 * - При `error` ставится `aria-invalid="true"` + `aria-describedby` указывает на
 *   `<p id={errorId}>` с текстом ошибки (screen reader озвучивает после name).
 * - При `hint` (без error) — `aria-describedby` указывает на `<p id={hintId}>`.
 * - `focus-visible:ring-2 focus-visible:ring-cta` — видимый focus для клавиатуры.
 * - min-h-tap (44px) — click target.
 *
 * Стиль: `border-[1.5px] border-accent rounded-xl bg-card` (Tailwind config v0.3,
 * ADR-0004). Без расширения config — комбинация существующих токенов (Q10).
 */
export interface TextInputProps {
  /** Уникальное имя поля; используется для form submit и data-testid. */
  name: string;
  /** Видимая подпись над полем. */
  label: string;
  /** Текущее значение (controlled). */
  value: string;
  /** Колбэк на каждое изменение. */
  onChange: (value: string) => void;
  /** Inline-ошибка валидации; рендерится под полем + меняет border на error. */
  error?: string;
  /** Подсказка под полем (рендерится только при отсутствии error). */
  hint?: string;
  /** HTML5 type — text по умолчанию. */
  type?: InputHTMLAttributes<HTMLInputElement>['type'];
  /** autoComplete для браузеров и менеджеров паролей. */
  autoComplete?: string;
  /** inputMode для подсказки клавиатуре на мобильных. */
  inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  /** Placeholder-текст внутри поля. */
  placeholder?: string;
  /** disabled (например, во время submit). */
  disabled?: boolean;
  /** required-флаг для HTML5 (сама валидация — Zod в Container). */
  required?: boolean;
  /** Максимальная длина (UI-граница; настоящая граница — Zod в Container). */
  maxLength?: number;
}

export function TextInput({
  name,
  label,
  value,
  onChange,
  error,
  hint,
  type = 'text',
  autoComplete,
  inputMode,
  placeholder,
  disabled,
  required,
  maxLength,
}: TextInputProps) {
  const reactId = useId();
  const inputId = `${name}-${reactId}`;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;

  const describedBy = error ? errorId : hint ? hintId : undefined;
  const hasError = Boolean(error);

  return (
    <div className="flex flex-col">
      <label htmlFor={inputId} className="text-ink font-semibold text-base mb-2">
        {label}
      </label>
      <input
        id={inputId}
        name={name}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        inputMode={inputMode}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        maxLength={maxLength}
        aria-invalid={hasError || undefined}
        aria-describedby={describedBy}
        className={
          'min-h-tap rounded-xl bg-card text-ink text-base px-3 py-3 ' +
          'border-[1.5px] ' +
          (hasError ? 'border-error ' : 'border-accent ') +
          'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
          'disabled:bg-icon-bg disabled:text-muted disabled:cursor-not-allowed'
        }
      />
      {hasError ? (
        <p id={errorId} role="alert" className="text-error text-sm mt-1">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-muted text-sm mt-1">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
